import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { BasePasswordForm } from '@/components/core';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui-kit/form';
import { Input } from '@/components/ui-kit/input';
import { useAccountActivation } from '../../hooks/use-auth';
import { setPasswordFormDefaultValue, getSetPasswordFormValidationSchema } from './utils';

/**
 * SetPasswordForm Component
 *
 * For the activation flow specifically, this also collects firstName and lastName
 * (required by the /iam/v4/auth/activate endpoint).
 *
 * The BasePasswordForm manages its own internal password/confirmPassword form.
 * We attach firstName/lastName as supplementary fields collected before submitting.
 */

const fieldsSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
});

type ActivationFields = z.infer<typeof fieldsSchema>;

export const SetpasswordForm = ({ code }: { code: string }) => {
  const { t } = useTranslation();
  const { isPending, mutateAsync } = useAccountActivation();
  const [isCaptchaValid, setIsCaptchaValid] = useState(false);
  const [names, setNames] = useState<ActivationFields>({ firstName: '', lastName: '' });

  const captchaEnabled = (import.meta.env.VITE_CAPTCHA_SITE_KEY || '') !== '';

  const form = useForm<ActivationFields>({
    defaultValues: { firstName: '', lastName: '' },
    resolver: zodResolver(fieldsSchema),
    mode: 'onTouched',
  });

  const handleSubmit = async (password: string, submissionCode: string, captchaToken?: string) => {
    // Validate firstName/lastName before submit
    const ok = await form.trigger();
    if (!ok) return;
    if (captchaEnabled && !captchaToken) return;

    const { firstName, lastName } = form.getValues();
    await mutateAsync({
      password,
      code: submissionCode,
      firstName,
      lastName,
      captchaCode: captchaToken ?? '',
      projectKey: import.meta.env.VITE_X_BLOCKS_KEY || '',
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <Form {...form}>
        <form className="flex flex-col gap-4" onSubmit={(e) => e.preventDefault()}>
          <div className="grid grid-cols-2 gap-3">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-high-emphasis font-normal">{t('FIRST_NAME')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('FIRST_NAME')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-high-emphasis font-normal">{t('LAST_NAME')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('LAST_NAME')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </form>
      </Form>

      <BasePasswordForm
        code={code}
        onSubmit={handleSubmit}
        validationSchema={getSetPasswordFormValidationSchema(t)}
        defaultValues={setPasswordFormDefaultValue}
        isPending={isPending}
        isCaptchaValid={isCaptchaValid}
        onCaptchaValidation={setIsCaptchaValid}
      />
    </div>
  );
};

// Keep names available for tests / ext. consumers
export { setPasswordFormDefaultValue, getSetPasswordFormValidationSchema };
// Suppress unused-import lint for the legacy setPasswordFormDefaultValue when treeshaken
void setPasswordFormDefaultValue;
